import { ReactNode } from 'react';
import Typography, { TypographyProps } from '@mui/material/Typography';

import useSiteCurrency from 'hooks/useSiteCurrency';
import { formatPrice } from 'utils/priceFormatter';

interface PriceLabelProps extends Omit<TypographyProps, 'children'> {
  /** Numbers from the API, or the raw strings the import grid holds while editing. */
  value: number | string | null | undefined;
  /** Rendered immediately after the amount, inside the same line — e.g. a "more price bands" marker. */
  suffix?: ReactNode;
}

/**
 * Displays a price using the selected site's currency symbol, to two decimal places.
 *
 * Use this anywhere a price is shown, rather than formatting inline — currency is a per-site
 * setting, so a hard-coded '$' is wrong for any tenant that isn't configured that way, and
 * per-screen formatting is how a list ends up showing 1.5 next to 1.4975.
 */
export default function PriceLabel({ value, suffix, variant = 'body2', ...typographyProps }: PriceLabelProps) {
  const currencySymbol = useSiteCurrency();

  return (
    <Typography variant={variant} {...typographyProps}>
      {formatPrice(value, currencySymbol)}
      {suffix}
    </Typography>
  );
}
